import React, { useState, useEffect } from 'react';
import {
  Video,
  Clock,
  Users,
  FileText,
  RefreshCw,
  Brain,
  Mic,
  AlertTriangle,
  ExternalLink,
  CheckCircle,
  Circle,
  Link2,
  Search,
  X,
  Building2
} from 'lucide-react';
import { fathomService } from '../../services/fathomService';
import { FathomAnalytics, FathomMeeting } from '../../types/fathom';
import { clientIntelligenceService } from '../../services/clientIntelligenceService';
import { contactsService } from '../../services/supabaseService';

const FathomDashboard: React.FC = () => {
  const [stats, setStats] = useState<FathomAnalytics | null>(null);
  const [recentMeetings, setRecentMeetings] = useState<FathomMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkedIds, setLinkedIds] = useState<Set<number>>(new Set());
  const [linkingMeeting, setLinkingMeeting] = useState<FathomMeeting | null>(null);
  const [linkingStatus, setLinkingStatus] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!fathomService.isConfigured()) {
        setStats(null);
        setRecentMeetings([]);
        return;
      }

      const [meetings, linked] = await Promise.all([
        fathomService.getMeetings(),
        clientIntelligenceService.getLinkedRecordingIds(),
      ]);
      const analytics = fathomService.calculateAnalytics(meetings);

      setRecentMeetings(meetings);
      setStats(analytics);
      setLinkedIds(linked);
    } catch (err: any) {
      console.error('Error loading Fathom dashboard:', err);
      setError(err.message || 'Error cargando datos de Fathom');
      setStats(null);
      setRecentMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    fathomService.clearCache();
    try {
      await loadDashboardData();
    } finally {
      setSyncing(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  const formatDate = (iso: string): string => {
    try {
      return new Date(iso).toLocaleString('es-ES', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return iso;
    }
  };

  const getMeetingDuration = (m: FathomMeeting): number => {
    if (!m.recording_start_time || !m.recording_end_time) return 0;
    const start = new Date(m.recording_start_time).getTime();
    const end = new Date(m.recording_end_time).getTime();
    return Math.max(0, (end - start) / 1000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando dashboard de Fathom...</span>
      </div>
    );
  }

  if (!fathomService.isConfigured()) {
    return (
      <div className="text-center py-12">
        <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Fathom no configurado</h3>
        <p className="text-gray-500">
          Configura tu API Key de Fathom en la pestaña "Settings" para comenzar
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button onClick={loadDashboardData} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
                Reintentar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Dashboard Fathom</h2>
          <p className="text-gray-600">Reuniones grabadas y análisis con IA</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          <span>{syncing ? 'Sincronizando...' : 'Sincronizar'}</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Reuniones</p>
              <p className="text-2xl font-bold text-blue-900">{stats?.total_meetings || 0}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Video className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Con Resumen IA</p>
              <p className="text-2xl font-bold text-green-900">{stats?.meetings_with_summary || 0}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Brain className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Externas</p>
              <p className="text-2xl font-bold text-purple-900">{stats?.external_meetings || 0}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-orange-50 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Tiempo Total</p>
              <p className="text-2xl font-bold text-orange-900">
                {stats?.total_duration_seconds ? formatDuration(stats.total_duration_seconds) : '0m'}
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Meetings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Reuniones Recientes</h3>
        </div>

        <div className="p-6">
          {recentMeetings.length > 0 ? (
            <div className="space-y-4">
              {recentMeetings.map((meeting) => (
                <div key={meeting.recording_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <Video className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {meeting.title || meeting.meeting_title || 'Sin título'}
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatDate(meeting.recording_start_time || meeting.created_at)}
                          {' · '}
                          {formatDuration(getMeetingDuration(meeting))}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <Users className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-600">
                            {meeting.calendar_invitees?.length || 0} participantes
                          </span>
                          {meeting.recorded_by && (
                            <span className="text-xs text-gray-400">
                              · Grabado por {meeting.recorded_by.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full flex-shrink-0 ml-2 ${
                      meeting.calendar_invitees_domains_type === 'one_or_more_external'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {meeting.calendar_invitees_domains_type === 'one_or_more_external' ? 'Externa' : 'Interna'}
                    </span>
                  </div>

                  {/* Summary preview */}
                  {meeting.default_summary?.markdown_formatted && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-1 mb-1">
                        <Brain className="w-3 h-3 text-purple-600" />
                        <span className="text-xs font-medium text-purple-700">Resumen IA</span>
                      </div>
                      <p className="text-xs text-gray-700 line-clamp-3">
                        {meeting.default_summary.markdown_formatted.substring(0, 300)}
                        {meeting.default_summary.markdown_formatted.length > 300 ? '...' : ''}
                      </p>
                    </div>
                  )}

                  {/* Action Items */}
                  {meeting.action_items && meeting.action_items.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">
                        Action Items ({meeting.action_items.filter(ai => ai.completed).length}/{meeting.action_items.length})
                      </p>
                      <div className="space-y-1">
                        {meeting.action_items.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-2 text-xs">
                            {item.completed ? (
                              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <Circle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                            )}
                            <span className={item.completed ? 'text-gray-400 line-through' : 'text-gray-700'}>
                              {item.description}
                              {item.assignee?.name && (
                                <span className="text-gray-400"> — {item.assignee.name}</span>
                              )}
                            </span>
                          </div>
                        ))}
                        {meeting.action_items.length > 3 && (
                          <p className="text-xs text-gray-400 ml-5">
                            +{meeting.action_items.length - 3} más
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Links */}
                  <div className="flex items-center space-x-4 text-xs">
                    {meeting.url && (
                      <a
                        href={meeting.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                      >
                        <Video className="w-3 h-3" />
                        <span>Ver en Fathom</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {meeting.share_url && (
                      <a
                        href={meeting.share_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-1 text-green-600 hover:text-green-800"
                      >
                        <FileText className="w-3 h-3" />
                        <span>Compartir</span>
                      </a>
                    )}
                    {linkedIds.has(meeting.recording_id) ? (
                      <span className="flex items-center space-x-1 text-emerald-600">
                        <CheckCircle className="w-3 h-3" />
                        <span>Vinculado</span>
                      </span>
                    ) : (
                      <button
                        onClick={() => setLinkingMeeting(meeting)}
                        className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-800"
                      >
                        <Link2 className="w-3 h-3" />
                        <span>Vincular con cliente</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Mic className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No hay reuniones</h4>
              <p className="text-gray-500">
                Las reuniones grabadas con Fathom aparecerán aquí
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action Items Summary */}
      {stats && stats.total_action_items > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Resumen de Action Items</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.total_action_items}</div>
                <p className="text-sm text-gray-600">Total</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{stats.completed_action_items}</div>
                <p className="text-sm text-gray-600">Completados</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {stats.total_action_items > 0
                    ? Math.round((stats.completed_action_items / stats.total_action_items) * 100)
                    : 0}%
                </div>
                <p className="text-sm text-gray-600">Completados</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Performance */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Métricas</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {stats?.average_duration_seconds ? formatDuration(stats.average_duration_seconds) : '0m'}
              </div>
              <p className="text-sm text-gray-600">Duración Promedio</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {stats?.total_meetings
                  ? Math.round((stats.meetings_with_summary / stats.total_meetings) * 100)
                  : 0}%
              </div>
              <p className="text-sm text-gray-600">Con Resumen</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {stats?.internal_meetings || 0}
              </div>
              <p className="text-sm text-gray-600">Internas</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {stats?.external_meetings || 0}
              </div>
              <p className="text-sm text-gray-600">Externas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Last Sync */}
      {stats?.last_sync && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            <span>Última sincronización: {new Date(stats.last_sync).toLocaleString('es-ES')}</span>
          </div>
        </div>
      )}

      {/* Link Meeting Modal */}
      {linkingMeeting && (
        <LinkMeetingModal
          meeting={linkingMeeting}
          onClose={() => { setLinkingMeeting(null); setLinkingStatus(null); }}
          onLinked={() => {
            setLinkedIds(prev => new Set([...prev, linkingMeeting.recording_id]));
            setLinkingMeeting(null);
            setLinkingStatus(null);
          }}
        />
      )}
    </div>
  );
};

// ================================================================
// Link Meeting to Client Modal
// ================================================================

const LinkMeetingModal: React.FC<{
  meeting: FathomMeeting;
  onClose: () => void;
  onLinked: () => void;
}> = ({ meeting, onClose, onLinked }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [suggestedContacts, setSuggestedContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      // Load all contacts
      const contacts = await contactsService.getAll();
      setAllContacts(contacts);

      // Auto-match by email
      const inviteeEmails = meeting.calendar_invitees
        ?.map(i => i.email?.toLowerCase())
        .filter(Boolean) as string[];

      if (inviteeEmails.length > 0) {
        const matched = contacts.filter(c =>
          c.email && inviteeEmails.includes(c.email.toLowerCase())
        );
        setSuggestedContacts(matched);
      }
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (contactId: string) => {
    setLinking(true);
    setError(null);
    try {
      await clientIntelligenceService.linkFathomMeeting(contactId, meeting);
      setSuccess(true);
      setTimeout(onLinked, 1500);
    } catch (err: any) {
      setError(err.message || 'Error vinculando reunión');
    } finally {
      setLinking(false);
    }
  };

  const filteredContacts = searchTerm.trim()
    ? allContacts.filter(c =>
        c.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.company?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Vincular con Cliente</h3>
            <p className="text-sm text-gray-500 mt-0.5">{meeting.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 flex-1 overflow-y-auto space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-green-800">Reunión vinculada e indexada correctamente</p>
              <p className="text-xs text-green-600 mt-1">La transcripción estará disponible en la Inteligencia del Cliente</p>
            </div>
          )}

          {!success && (
            <>
              {/* Auto-suggested contacts */}
              {suggestedContacts.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Coincidencias por email</p>
                  <div className="space-y-2">
                    {suggestedContacts.map(contact => (
                      <div key={contact.id} className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{contact.fullName}</p>
                          <p className="text-xs text-gray-500">{contact.email}{contact.company ? ` · ${contact.company}` : ''}</p>
                        </div>
                        <button
                          onClick={() => handleLink(contact.id)}
                          disabled={linking}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {linking ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          ) : (
                            <Link2 className="w-3 h-3" />
                          )}
                          <span>Vincular</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                  {suggestedContacts.length > 0 ? 'O buscar otro cliente' : 'Buscar cliente'}
                </p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Buscar por nombre, email o empresa..."
                  />
                </div>
              </div>

              {/* Search results */}
              {filteredContacts.length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {filteredContacts.slice(0, 10).map(contact => (
                    <div key={contact.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{contact.fullName}</p>
                        <p className="text-xs text-gray-500">
                          {contact.email}{contact.company ? ` · ${contact.company}` : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => handleLink(contact.id)}
                        disabled={linking}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {linking ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        ) : (
                          <Link2 className="w-3 h-3" />
                        )}
                        <span>Vincular</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {searchTerm && filteredContacts.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No se encontraron clientes</p>
              )}

              {loading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FathomDashboard;
